import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, ChevronRight, Users, User, Banknote, Receipt, Activity, Ticket, Dices, Radio, LogOut, Percent, Gift, Copy, Check, Share2,
} from "lucide-react";
import { referralApi } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { useApp } from "../context/AppContext";
import { inr } from "../lib/format";

const ComingSoon = ({ note }) => (
  <div className="flex items-center gap-2 text-sm text-slate-500">
    <span className="rounded-full bg-orange-100 text-orange-600 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">Coming soon</span>
    {note}
  </div>
);

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800">{value || "—"}</span>
  </div>
);

function ReferEarn() {
  const { toast } = useToast();
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { referralApi.get().then(setRef).catch(() => {}); }, []);
  if (!ref) return <p className="text-sm text-slate-500">Loading your referral code…</p>;
  const link = `https://batballworld.com/?ref=${ref.code}`;
  const msg = `Join me on BatBallWorld 🏏 Sign up with my referral code ${ref.code} and we both get a bonus! ${link}`;
  const copy = () => {
    navigator.clipboard?.writeText(msg);
    setCopied(true);
    toast({ title: "Referral link copied!" });
    setTimeout(() => setCopied(false), 1500);
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "BatBallWorld", text: msg, url: link }); return; } catch { /* fall back to copy */ }
    }
    copy();
  };
  return (
    <div className="space-y-2.5" data-testid="acc-refer-earn">
      <p className="text-sm text-slate-600">
        Your friend gets <b>{inr(ref.refereeBonus)}</b> on signup and you earn <b>{inr(ref.referrerBonus)}</b> once they deposit {inr(ref.minDeposit || 2000)} or more.
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded-md bg-white border border-slate-300 px-3 py-2 font-display font-bold tracking-wider text-[#1f3b5b]" data-testid="acc-referral-code">{ref.code}</code>
        <button onClick={copy} className="grid place-items-center h-10 w-10 rounded-md border border-[#1f3b5b] text-[#1f3b5b] bg-white" data-testid="acc-referral-copy-btn" aria-label="Copy referral link">
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      <div className="rounded-md bg-white border border-slate-200 px-3 py-2 text-[11px] text-slate-500 break-all" data-testid="acc-referral-link">{link}</div>
      <button onClick={share} className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-[#f5b400] text-black text-sm font-extrabold py-2" data-testid="acc-referral-share-btn">
        <Share2 className="h-4 w-4" /> Share link &amp; code
      </button>
      <div className="flex gap-4 text-sm text-slate-600">
        <span>Invited: <b className="text-slate-900" data-testid="acc-referral-count">{ref.count}</b></span>
        <span>Earned: <b className="text-emerald-700" data-testid="acc-referral-earned">{inr(ref.earnings)}</b></span>
      </div>
    </div>
  );
}

export default function AccountAccordion({ user, balance }) {
  const { logout } = useApp();
  const [open, setOpen] = useState(null);
  const [lbActive, setLbActive] = useState(() => (typeof window !== "undefined" ? localStorage.getItem("cb_losing_bonus") === "1" : false));
  const setLB = (v) => { localStorage.setItem("cb_losing_bonus", v ? "1" : "0"); setLbActive(v); };
  const toggle = (key) => setOpen((cur) => (cur === key ? null : key));

  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;
  const lastLogin = typeof window !== "undefined" ? localStorage.getItem("cb_last_login") : null;
  const lastLogout = typeof window !== "undefined" ? localStorage.getItem("cb_last_logout") : null;

  const ITEMS = [
    {
      key: "affiliate", label: "Affiliate Account", icon: Users,
      content: (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">Create your affiliate account, invite members, and earn 10% of their net losses.</p>
          <Link to="/affiliate" className="inline-flex items-center gap-1 rounded-full bg-teal-500 text-black text-sm font-extrabold px-4 py-1.5" data-testid="acc-affiliate-link">
            Open affiliate account <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      ),
    },
    {
      key: "refer-earn", label: "Refer & Earn", icon: Gift,
      content: <ReferEarn />,
    },
    {
      key: "account-details", label: "Account Details", icon: User,
      content: (
        <div>
          <Field label="Name" value={user?.name} />
          <Field label="Username" value={user?.username && `@${user.username}`} />
          <Field label="Phone" value={user?.phone} />
          {user?.referralCode && <Field label="Referral code" value={user.referralCode} />}
          <Field label="Balance" value={inr(balance || 0)} />
          {memberSince && <Field label="Member since" value={memberSince} />}
        </div>
      ),
    },
    {
      key: "balance-withdraw", label: "Balance withdraw", icon: Banknote,
      content: (
        <div className="space-y-3">
          <Field label="Available balance" value={inr(balance || 0)} />
          <Link to="/wallet?tab=withdraw" className="inline-flex items-center gap-1 rounded-full bg-[#E0B84C] text-black text-sm font-extrabold px-4 py-1.5" data-testid="acc-withdraw-cta">
            Withdraw funds <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      ),
    },
    {
      key: "game-history", label: "Game History", icon: Receipt,
      content: (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">Your casino game rounds from the last 15 days. Older rounds are removed automatically.</p>
          <Link to="/game-history" className="inline-flex items-center gap-1 rounded-full bg-[#1f3b5b] text-white px-4 py-1.5 text-sm font-semibold hover:bg-[#2a4d75] transition-colors" data-testid="acc-game-history-link">
            View game history <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      ),
    },
    {
      key: "activity-log", label: "Activity Log", icon: Activity,
      content: (
        <div>
          <Field label="Last login" value={lastLogin ? new Date(lastLogin).toLocaleString("en-IN") : "—"} />
          <Field label="Last logout" value={lastLogout ? new Date(lastLogout).toLocaleString("en-IN") : "—"} />
        </div>
      ),
    },
    {
      key: "my-bets", label: "My Bets", icon: Ticket,
      content: (
        <Link to="/my-bets" className="inline-flex items-center gap-1 rounded-full bg-slate-200 text-slate-800 px-4 py-1.5 text-sm font-semibold hover:bg-slate-300 transition-colors" data-testid="acc-mybets-link">
          View my bets <ChevronRight className="h-4 w-4" />
        </Link>
      ),
    },
    { key: "slot-bets", label: "Slot Bets", icon: Dices, to: "/slot-bets" },
    { key: "live-casino-bets", label: "Live Casino Bets", icon: Radio, to: "/live-casino-bets" },
    {
      key: "losing-bonus", label: "Losing Bonus", icon: Percent,
      content: user?.affiliateBy ? (
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-600" data-testid="losing-bonus-blocked">
          Not eligible — accounts created under an affiliate cannot claim the Losing Bonus.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Status</span>
            <span
              data-testid="losing-bonus-status"
              className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${lbActive ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-500"}`}
            >
              {lbActive ? "Active" : "Inactive"}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLB(true)}
              disabled={lbActive}
              data-testid="losing-bonus-activate"
              className="flex-1 rounded-full bg-emerald-500 text-black text-sm font-bold py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-400 transition"
            >
              Activate
            </button>
            <button
              type="button"
              onClick={() => setLB(false)}
              disabled={!lbActive}
              data-testid="losing-bonus-deactivate"
              className="flex-1 rounded-full bg-red-50 border border-red-200 text-red-500 text-sm font-bold py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-100 transition"
            >
              Deactivate
            </button>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">How it works</div>
            <ul className="space-y-1.5 text-sm text-slate-500 list-disc pl-4">
              <li>Activate the bonus first — it stays off by default and must be on to qualify.</li>
              <li>Play on the platform every day for 7 days in a row.</li>
              <li>Make at least one deposit each of those 7 days.</li>
              <li>Miss even a single day (no play or no deposit) and the bonus is void.</li>
              <li>Meet all criteria and you get <b className="text-slate-800">10% of your total net loss over the 7 days back</b> as a refund.</li>
              <li>The refund is credited only when every condition above is met.</li>
            </ul>
          </div>
          <ComingSoon note="Auto-tracking & payout go live soon." />
        </div>
      ),
    },
  ];

  const Arrow = ({ open: o }) => (
    <span className="grid place-items-center h-7 w-7 rounded-[4px] border border-[#bdbdbd] bg-white text-[#222] shrink-0">
      {o ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </span>
  );
  const rowCls = "w-full flex items-center justify-between gap-3 h-[58px] pl-4 pr-4 text-left bg-white border-b border-[#e5e5e5] hover:bg-[#fafafa] transition-colors";
  const labelCls = "flex-1 min-w-0 font-bold text-[15px] truncate text-transparent bg-clip-text bg-[linear-gradient(180deg,#2a2a2a_0%,#000_45%,#3a3a3a_100%)]";

  return (
    <div className="w-full bg-white" data-testid="account-accordion">
      <div className="flex items-center justify-between bg-black text-white px-4 h-11 text-[13px]" data-testid="account-user-strip">
        <span className="flex items-center gap-2 min-w-0">
          <span className="grid place-items-center h-6 w-6 rounded-full bg-slate-300 text-slate-700"><User className="h-3.5 w-3.5" /></span>
          <span className="font-semibold truncate">{user?.username || user?.phone}</span>
        </span>
        <span className="text-slate-300 shrink-0">GMT+5:30</span>
      </div>
      {ITEMS.map(({ key, label, content, to }) => {
        const isOpen = open === key;
        if (to) {
          return (
            <div key={key} data-testid={`account-row-${key}`}>
              <Link to={to} data-testid={`acc-row-${key}`} className={rowCls}>
                <span className={labelCls}>{label}</span>
                <Arrow open={false} />
              </Link>
            </div>
          );
        }
        return (
          <div key={key} data-testid={`account-row-${key}`}>
            <button type="button" onClick={() => toggle(key)} aria-expanded={isOpen} data-testid={`acc-row-${key}`} className={rowCls}>
              <span className={labelCls}>{label}</span>
              <Arrow open={isOpen} />
            </button>
            {isOpen && (
              <div className="px-4 pb-4 pt-2 bg-slate-100 border-b border-[#e5e5e5]" data-testid={`acc-panel-${key}`}>
                {content}
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={logout}
        data-testid="account-logout-btn"
        className="w-full h-[58px] flex items-center justify-center text-white font-bold uppercase tracking-[1px] text-[15px] border-y-2 border-[#A81C10] bg-[linear-gradient(to_bottom,#E5321F,#C42316)] active:brightness-95 transition-[filter]"
      >
        LOGOUT
        <LogOut className="h-4 w-4 ml-2 text-white" strokeWidth={2.5} />
      </button>
      <div className="h-[70px] bg-[#F2EEE6]" aria-hidden="true" />
    </div>
  );
}
